<template>
  <div>
    <SummarizationCard title='Legal Representative of a Child'>
      <SummarizationItem>
        <template v-slot:title>
          Selected Legal Representative
        </template>
        <p class="m-0">
          {{ legalRepresentative.relationship.label }}
        </p>
      </SummarizationItem>
    </SummarizationCard>

    <SummarizationCard title='Personal Data of the Legal Representative'>
      <SummarizationItem>
        <template v-slot:title>
          Name and Surname
        </template>
        <p class="m-0">
          {{ legalRepresentative.name + ' ' + legalRepresentative.surname }}
        </p>
      </SummarizationItem>
      <SummarizationItem>
        <template v-slot:title>
          Nationality
        </template>
        <p class="m-0">
          {{ legalRepresentative.nationality.label }}
        </p>
      </SummarizationItem>
    </SummarizationCard>

    <SummarizationCard title='Identity Verification of the Legal Representative'>
      <SummarizationItem>
        <template v-if="legalRepresentative.hasBirthNumber" v-slot:title>
          Birth Number
        </template>
        <template v-else v-slot:title>
          BIČ
        </template>
        <p class="m-0">
          {{ legalRepresentative.birthNumber }}
        </p>
      </SummarizationItem>
      <SummarizationItem>
        <template v-slot:title v-if="isID" >ID Card Number</template>
        <template v-slot:title v-else-if="isPassport" >Passport Number</template>
        <template v-slot:title v-else-if="isResidencePermit" >Permit Number</template>
        <template v-slot:title v-else-if="isZiadost" >Application Number</template>
        <p class="m-0">
          {{ legalRepresentative.evidenceNumber }}
        </p>
      </SummarizationItem>
      <SummarizationItem>
        <template v-if="isID || isPassport || isResidencePermit" v-slot:title>
          Date Issued
        </template>
        <template v-else v-slot:title>
          Date of Application
        </template>
        <p v-if="legalRepresentative.validityOfDocumentFrom" class="m-0">
          {{ formatDate(legalRepresentative.validityOfDocumentFrom) }}
        </p>
        <p v-else-if="legalRepresentative.validityOfZiadost" class="m-0">
          {{ formatDate(legalRepresentative.validityOfZiadost) }}
        </p>
      </SummarizationItem>
      <SummarizationItem v-if="!isZiadost">
        <template v-if="isID || isPassport" v-slot:title>
          Date of Validity
        </template>
        <template v-else-if="isResidencePermit" v-slot:title>
          Valid Until
        </template>
        <p v-if="legalRepresentative.validityOfDocumentTo" class="m-0">
          {{ formatDate(legalRepresentative.validityOfDocumentTo) }}
        </p>
      </SummarizationItem>
      <SummarizationItem v-if="legalRepresentative.evidenceType === enums.EvidenceTypeEnum.RESIDENCE_PERMIT">
        <template v-slot:title>
          Validity of Stay Until
        </template>
        <p class="m-0">
          {{ formatDate(legalRepresentative.validityOfPermanentResidence) }}
        </p>
      </SummarizationItem>
    </SummarizationCard>

    <SummarizationCard title='Information on the Residence of the Legal Representative'>
      <SummarizationItem>
        <template v-slot:title>
          Permanent Residence Address
        </template>
        <p class="m-0">
          {{ `${legalRepresentative.address.street} ${legalRepresentative.address.streetNumber}` }}
        </p>
        <p class="m-0">
          {{ `${legalRepresentative.address.zip} ${legalRepresentative.address.city}` }}
        </p>
        <p class="m-0">
          {{ `${legalRepresentative.address.country ? legalRepresentative.address.country.label : ''}` }}
        </p>
      </SummarizationItem>
      <SummarizationItem>
        <template v-slot:title>
          Mailing Address
        </template>
        <p v-if="legalRepresentative.postalAddressSameAsAddress" class="m-0">
          Same as Permanent Residence Address
        </p>
        <p v-else-if="addressNotFilled(legalRepresentative.postalAddress) && legalRepresentative.hasTemporaryResidence">
          Same as Temporary Residence Address
        </p>
        <div v-else>
          <p class="m-0">
            {{ `${legalRepresentative.postalAddress.street} ${legalRepresentative.postalAddress.streetNumber}` }}
          </p>
          <p class="m-0">
            {{ `${legalRepresentative.postalAddress.zip} ${legalRepresentative.postalAddress.city}` }}
          </p>
          <p class="m-0">
            {{ `${legalRepresentative.postalAddress.country ? legalRepresentative.postalAddress.country.label : ''}` }}
          </p>
        </div>
      </SummarizationItem>
      <SummarizationItem v-if="legalRepresentative.hasTemporaryResidence">
        <template v-slot:title>
          Temporary Address
        </template>
        <p class="m-0">
          {{ `${legalRepresentative.temporaryResidence.street} ${legalRepresentative.temporaryResidence.streetNumber}` }}
        </p>
        <p class="m-0">
          {{ `${legalRepresentative.temporaryResidence.zip} ${legalRepresentative.temporaryResidence.city}` }}
        </p>
        <p class="m-0">
          {{ `${legalRepresentative.temporaryResidence.country ? legalRepresentative.temporaryResidence.country.label : ''}` }}
        </p>
      </SummarizationItem>
      <SummarizationItem v-if="insuredPersonType !== this.enums.InsuredPersonTypeEnum.CHANGE_OF_INSURANCE_COMPANY_CHILD">
        <template v-slot:title>
          Do you wish to also indicate another legal representative for the child?
        </template>
        <p v-if="secondLegalRepresentative.iAmInterestedEnterSecondLegalRepresentative" class="m-0">
          Yes
        </p>
        <p v-else class="m-0">
          No
        </p>
      </SummarizationItem>
      <SummarizationItem v-if="secondLegalRepresentative.iAmInterestedEnterSecondLegalRepresentative">
        <template v-slot:title>
          Nationality of the Other Legal Representative
        </template>
        <p class="m-0">
          {{ secondLegalRepresentative.nationality.label }}
        </p>
      </SummarizationItem>
      <SummarizationItem v-if="secondLegalRepresentative.iAmInterestedEnterSecondLegalRepresentative">
        <template v-slot:title>
          Birth Number of the Other Legal Representative
        </template>
        <p class="m-0">
          {{ secondLegalRepresentative.birthNumber }}
        </p>
      </SummarizationItem>
    </SummarizationCard>
  </div>
</template>
<script>
import SummarizationCard from '@/components/SummarizationCard/SummarizationCard.vue';
import SummarizationItem from '@/components/SummarizationItem/SummarizationItem.vue';
import enums from '@/mixins/enums';
import { addressNotFilled } from '../utils/address';

export default {
  components: { SummarizationItem, SummarizationCard },
  mixins: [enums],
  props: {
    model: { type: Object, required: true },
    legalRepresentative: { type: Object, required: true },
    secondLegalRepresentative: { type: Object, required: true },
    insuredPersonType: { type: Object, required: true },
  },
  computed: {
    isPassport() {
      const { EvidenceTypeEnum: enumeration } = this.enums;
      return this.legalRepresentative.evidenceType === enumeration.PASSPORT;
    },
    isID() {
      const { EvidenceTypeEnum: enumeration } = this.enums;
      return this.legalRepresentative.evidenceType === enumeration.ID;
    },
    isZiadost() {
      const { EvidenceTypeEnum: enumeration } = this.enums;
      return this.legalRepresentative.evidenceType === enumeration.ZIADOST;
    },
    isResidencePermit() {
      const { EvidenceTypeEnum: enumeration } = this.enums;
      return this.legalRepresentative.evidenceType === enumeration.RESIDENCE_PERMIT;
    },
  },
  methods: {
    addressNotFilled,
    formatDate(date) {
      return `${this.formatDateNumber(date.day)}.${this.formatDateNumber(date.month)}.${this.formatDateNumber(date.year)}`;
    },
    formatDateNumber(number) {
      if (number) {
        return number.toString().padStart(2, '0');
      }
      return '';
    },
  },
};
</script>
